import React, { useMemo, useState } from 'react';
import { ChevronDown, UsersRound } from 'lucide-react';

/* ── COVERAGE ────────────────────────────────────────────────────────────────────────────────
 * What the CRM connection reveals about the customer's own position, as opposed to what Neo
 * caused. It sits ABOVE the loop funnel on the Pipeline tab on purpose: "here is your market and
 * where you are thin" is the question a GTM lead arrives with, and it frames the funnel below it.
 *
 * 🔴 EVERY FIGURE HERE IS SAMPLE DATA AND THE BAND SAYS SO. These charts are shaped for the
 * onboarding analysis (match the CRM against the market, measure depth, measure role coverage);
 * the analysis itself is not wired yet. An unlabelled mock on a page that otherwise shows live
 * CRM data would be read as the customer's real coverage, which is the one failure mode worth
 * designing against. The chip next to the heading is not decoration — do not remove it until the
 * data behind it is real.
 *
 * 🔴 AGGREGATE ONLY — NO NAMED PEOPLE, NO MESSAGE CONTENT. Analytics shows gaps; Today's Plan
 * names people. The same fact is welcome in one place and reads as surveillance in the other, and
 * a GTM lead who feels watched stops bringing the tool to their team.
 *
 * WHEN THE REAL DATA LANDS, replace SAMPLE below with the endpoint payload. Each field names the
 * source it should come from; none of it needs a scope we do not already hold.
 */

const SAMPLE = {
  // market  : count of accounts in the tenant's SAM for its lens  (Neo: tenant_account_scores)
  // active  : matched to a CRM company WITH an engagement inside the staleness window
  // stale   : matched, but no engagement inside the window and no open deal
  // untouched: in the market, no matching CRM company at all
  market: 912,
  active: 140,
  stale: 63,
  // depth: one point per OPEN-PIPELINE account — fit from Neo, contacts from CRM associations
  // with a logged interaction. Deliberately not every account: depth only means something where
  // there is something to lose.
  depth: [
    { fit: 88, contacts: 1 }, { fit: 86, contacts: 5 }, { fit: 84, contacts: 1 },
    { fit: 83, contacts: 4 }, { fit: 81, contacts: 1 }, { fit: 79, contacts: 2 },
    { fit: 78, contacts: 1 }, { fit: 77, contacts: 6 }, { fit: 76, contacts: 1 },
    { fit: 75, contacts: 3 }, { fit: 74, contacts: 0 }, { fit: 73, contacts: 4 },
    { fit: 72, contacts: 1 }, { fit: 71, contacts: 2 }, { fit: 70, contacts: 1 },
    { fit: 69, contacts: 5 }, { fit: 68, contacts: 0 }, { fit: 67, contacts: 3 },
    { fit: 66, contacts: 1 }, { fit: 65, contacts: 1 }, { fit: 64, contacts: 4 },
    { fit: 63, contacts: 2 }, { fit: 62, contacts: 3 }, { fit: 61, contacts: 1 },
    { fit: 59, contacts: 2 }, { fit: 58, contacts: 4 }, { fit: 57, contacts: 1 },
    { fit: 55, contacts: 3 }, { fit: 54, contacts: 2 }, { fit: 52, contacts: 1 },
    { fit: 51, contacts: 5 }, { fit: 49, contacts: 2 }, { fit: 47, contacts: 1 },
    { fit: 46, contacts: 3 }, { fit: 44, contacts: 2 }, { fit: 42, contacts: 1 },
    { fit: 40, contacts: 2 }, { fit: 38, contacts: 1 }, { fit: 36, contacts: 3 },
    { fit: 34, contacts: 1 }, { fit: 31, contacts: 2 }, { fit: 28, contacts: 1 },
  ],
  // roles: % of OPEN-PIPELINE accounts holding at least one contact mapped to that buying role.
  // Source: their contacts' job titles put through Neo's role taxonomy — the same taxonomy that
  // scores personas, so the labels mean the same thing in both places.
  roles: [
    { role: 'IT / systems director', pct: 82 },
    { role: 'Head of assessment', pct: 47 },
    { role: 'Academic registrar', pct: 18 },
    { role: 'Procurement lead', pct: 14 },
    { role: 'PVC Education', pct: 9, signsOff: true },
  ],
};

/* The threshold that defines the risk quadrant. A high-fit account known through one person is
 * the shape worth acting on; the same depth at fit 35 is correct behaviour, not a gap. */
const RISK_FIT = 65;
const RISK_CONTACTS = 1;

const StackedCoverage = ({ market, active, stale }) => {
  const untouched = Math.max(0, market - active - stale);
  const seg = [
    { key: 'active', n: active, label: 'Active', cls: 'bg-[#0A1A2F]', text: 'text-white' },
    { key: 'stale', n: stale, label: 'Stale', cls: 'bg-[#00A8A8]', text: 'text-white' },
    { key: 'untouched', n: untouched, label: 'Never touched', cls: 'bg-gray-100', text: 'text-[#0A1A2F]' },
  ];
  const pct = (n) => (market > 0 ? (n / market) * 100 : 0);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <h4 className="text-base font-semibold text-[#0A1A2F]">Market coverage</h4>
        <p className="text-sm text-gray-400 whitespace-nowrap tabular-nums">
          {market.toLocaleString()} accounts in your market
        </p>
      </div>
      <div className="flex w-full h-12 rounded overflow-hidden" role="img"
        aria-label={`Active ${active}, stale ${stale}, never touched ${untouched} of ${market}`}>
        {seg.filter((s) => s.n > 0).map((s) => (
          <div key={s.key} className={`${s.cls} flex items-center justify-center min-w-[52px]`}
            style={{ width: `${pct(s.n)}%` }}>
            <span className={`text-sm font-semibold tabular-nums ${s.text}`}>
              {s.n.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2">
        {seg.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${s.cls} ${
              s.key === 'untouched' ? 'border border-gray-300' : ''}`} />
            <span className="text-xs text-gray-500">
              {s.label} <span className="tabular-nums">{Math.round(pct(s.n))}%</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DepthScatter = ({ points }) => {
  /* One scale, declared once and used for the marks, the ticks and the risk band — so a dot can
   * never sit somewhere its axis label does not claim. The viewBox carries padding for the
   * outermost labels rather than letting them clip at the edge. */
  const W = 560, H = 268;
  const L = 38, R = 10, T = 14, B = 38;
  const X0 = 20, X1 = 100, Y1 = 6;
  const px = (fit) => L + ((fit - X0) / (X1 - X0)) * (W - L - R);
  const py = (c) => (H - B) - (Math.min(c, Y1) / Y1) * (H - B - T);

  const risk = points.filter((p) => p.fit >= RISK_FIT && p.contacts <= RISK_CONTACTS);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <h4 className="text-base font-semibold text-[#0A1A2F]">Depth vs fit</h4>
        <p className="text-sm text-gray-400 whitespace-nowrap">Open pipeline</p>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img"
          aria-label={`${points.length} open-pipeline accounts by account fit and contacts held; ${risk.length} sit above fit ${RISK_FIT} with ${RISK_CONTACTS} contact or fewer`}>
          {/* the quadrant worth acting on, drawn behind everything */}
          {/* Half a step above the threshold, so a dot sitting exactly ON the boundary (contacts
            * === RISK_CONTACTS, which is most of them) is drawn fully INSIDE the band rather than
            * straddling its edge and reading as ambiguous. */}
          <rect x={px(RISK_FIT)} y={py(RISK_CONTACTS + 0.5)} width={W - R - px(RISK_FIT)}
            height={(H - B) - py(RISK_CONTACTS + 0.5)} fill="#FEF3C7" />
          {/* y gridlines + ticks */}
          {[0, 2, 4, 6].map((c) => (
            <g key={c}>
              <line x1={L} y1={py(c)} x2={W - R} y2={py(c)} stroke="#F1F5F9" strokeWidth="1" />
              <text x={L - 8} y={py(c) + 4} textAnchor="end" fontSize="11" fill="#94A3B8">{c}</text>
            </g>
          ))}
          {/* x ticks */}
          {[20, 40, 60, 80, 100].map((f) => (
            <text key={f} x={px(f)} y={H - B + 16} textAnchor="middle" fontSize="11" fill="#94A3B8">
              {f}
            </text>
          ))}
          <line x1={L} y1={H - B} x2={W - R} y2={H - B} stroke="#E2E8F0" strokeWidth="1" />
          {points.map((p, i) => {
            const at = p.fit >= RISK_FIT && p.contacts <= RISK_CONTACTS;
            return (
              <circle key={i} cx={px(p.fit)} cy={py(p.contacts)} r={at ? 5 : 4}
                fill={at ? '#D97706' : '#00A8A8'} fillOpacity={at ? 1 : 0.65} />
            );
          })}
          <text x={(L + W - R) / 2} y={H - 4} textAnchor="middle" fontSize="11" fill="#64748B">
            Account fit
          </text>
          <text x={12} y={(T + H - B) / 2} textAnchor="middle" fontSize="11" fill="#64748B"
            transform={`rotate(-90 12 ${(T + H - B) / 2})`}>
            Contacts held
          </text>
        </svg>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#D97706] align-middle mr-1.5" />
        <span className="tabular-nums font-medium text-[#0A1A2F]">{risk.length}</span>{' '}
        accounts above fit {RISK_FIT} are known through one person or none.
      </p>
    </div>
  );
};

const RoleCoverage = ({ roles }) => (
  <div>
    <div className="flex items-baseline justify-between gap-4 mb-2">
      <h4 className="text-base font-semibold text-[#0A1A2F]">Role coverage</h4>
      <p className="text-sm text-gray-400 whitespace-nowrap">% of open pipeline</p>
    </div>
    <div className="flex flex-col gap-2.5">
      {roles.map((r) => (
        <div key={r.role} className="flex items-center gap-3">
          <div className="w-44 shrink-0 text-sm text-gray-600 truncate" title={r.role}>
            {r.role}
          </div>
          <div className="flex-1 h-5 bg-gray-100 rounded-sm overflow-hidden">
            <div className={`h-full ${r.signsOff ? 'bg-[#D97706]' : 'bg-[#0A1A2F]'}`}
              style={{ width: `${Math.max(0, Math.min(100, r.pct))}%` }} />
          </div>
          <div className="w-10 shrink-0 text-right text-sm tabular-nums text-[#0A1A2F]">
            {r.pct}%
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function CoverageBand() {
  const [open, setOpen] = useState(true);
  const d = SAMPLE;
  const held = useMemo(() => d.active + d.stale, [d.active, d.stale]);

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 bg-[#00C2C2]/10 rounded">
          <UsersRound className="w-4 h-4 text-[#00C2C2]" />
        </div>
        <h2 className="text-base font-bold text-[#0A1A2F]">Coverage</h2>
        {/* 🔴 Not decoration. See the header note — this stays until the data is real. */}
        <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200
          rounded px-1.5 py-0.5">
          Sample data
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="ml-auto flex items-center gap-1 text-sm text-gray-500 hover:text-[#0A1A2F]"
          aria-expanded={open}>
          {open ? 'Hide' : 'Show'}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} />
        </button>
      </div>

      {open && (
        <div className="border border-gray-200 rounded-lg p-5">

          <StackedCoverage market={d.market} active={d.active} stale={d.stale} />

          <p className="text-sm text-gray-500 mt-2.5">
            You hold <span className="tabular-nums font-medium text-[#0A1A2F]">{held}</span> of{' '}
            <span className="tabular-nums">{d.market.toLocaleString()}</span> accounts in your
            market, and{' '}
            <span className="tabular-nums font-medium text-[#0A1A2F]">{d.stale}</span> of those have
            gone quiet.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6 mt-6">
            <DepthScatter points={d.depth} />
            <RoleCoverage roles={d.roles} />
          </div>
        </div>
      )}
    </div>
  );
}
